import { createContext, useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { resetTicketDoc } from "../../redux/slices/ticketStore"; // Assuming this action resets certain state

// Create context
export const AuthContext = createContext({
  authenticate: () => {},
  reAuthenticate: () => {},
  checkTokenExpiry: () => {},
  logout: () => {}, // Logout should be a valid function
});

const AuthContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null); // State to store user data
  const [isDuplicateTab, setIsDuplicateTab] = useState(false);
  const channelRef = useRef(null);
  const timeoutRef = useRef(null);

  // Define the logout function
  const logout = (forceLogout = false) => {
    // Only clear authentication-related data instead of everything
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Clear other specific keys if they exist, or just clear and restore the flag
    localStorage.clear(); 
    sessionStorage.clear();
    
    // If it's a force logout (another tab is taking over), DON'T restore the tab active flag
    // Only restore if it's a normal logout (user clicked logout button)
    if (!forceLogout) {
      // For normal logout, we'll let the next login claim the session naturally
      // This prevents stale session flags
    }

    dispatch(resetTicketDoc()); 
    setUser(null); 
  };

  // Define authenticate function
  const authenticate = (userData) => {
    setUser(userData); // Store user data in state or perform any other login actions
    // You can store other details in localStorage or perform additional actions here
  };

  // Define reAuthenticate function if needed (for token refresh, etc.)
  const reAuthenticate = () => {
    // Add logic for re-authenticating the user (e.g., refreshing tokens)
  };

  // Define the token expiry check function
  const checkTokenExpiry = (token) => {
    let expiryTime = new Date(token?.expiry);
    return new Date() > new Date(expiryTime); // Returns true if the token is expired
  };

  // Enforce single active tab session - runs on mount and when user changes (login/logout)
  useEffect(() => {
    // Clear any previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const channel = new BroadcastChannel("app_session");

    const checkAndEnforceSession = () => {
      const token = localStorage.getItem("token");
      const isTabActive = sessionStorage.getItem("tab_session_active") === "true";
      console.log(`[Tab Debug] checkAndEnforceSession - token: ${!!token}, isTabActive: ${isTabActive}`);

      // If there's no token, we don't need to enforce the single tab check yet
      if (!token) {
        setIsDuplicateTab(false);
        return;
      }

      if (isTabActive) {
        // If we are already the active tab, just stay active
        console.log('[Tab Debug] This tab is already active master');
        setIsDuplicateTab(false);
      } else {
        // Request active tabs to identify themselves
        console.log('[Tab Debug] Posting check_session to channel');
        channel.postMessage("check_session");
      }
    };

    // Check session on mount and when user/token changes
    checkAndEnforceSession();

    // Store channel and timeout in refs so they persist across effect runs
    channelRef.current = channel;

    channel.onmessage = (event) => {
      console.log(`[Tab Debug] Received message: ${event.data}`);
      if (event.data === "check_session") {
        // If we have a valid token, respond that we're active
        // Use current sessionStorage value (not stale closure)
        const hasToken = localStorage.getItem("token");
        const isActive = sessionStorage.getItem("tab_session_active") === "true";
        console.log(`[Tab Debug] check_session received - hasToken: ${!!hasToken}, isActive: ${isActive}`);
        if (hasToken && isActive) {
          console.log('[Tab Debug] Responding with session_active');
          channel.postMessage("session_active");
        }
      } else if (event.data === "session_active") {
        // Another tab is already active - mark this as duplicate
        // Only if this tab hasn't claimed the session yet
        const isTabActive = sessionStorage.getItem("tab_session_active") === "true";
        console.log(`[Tab Debug] session_active received - this tab active: ${isTabActive}`);
        if (!isTabActive) {
          console.log('[Tab Debug] Marking as duplicate tab');
          sessionStorage.setItem("tab_session_marked_duplicate", "true");
          setIsDuplicateTab(true);
        }
      } else if (event.data === "force_logout") {
        // Another tab is claiming the session - logout this tab
        // Check current sessionStorage value dynamically
        const isActive = sessionStorage.getItem("tab_session_active") === "true";
        const isMarkedDuplicate = sessionStorage.getItem("tab_session_marked_duplicate") === "true";
        console.log(`[Tab Debug] force_logout received - this tab active: ${isActive}, isDuplicate: ${isMarkedDuplicate}`);
        // Only logout if this is the active tab AND not marked as duplicate
        // The duplicate tab (the one sending force_logout) should not logout
        if (isActive && !isMarkedDuplicate) {
          console.log('[Tab Debug] Force logging out this tab');
          logout(true); // true = force logout, don't restore session flags
          window.location.replace('/login');
        } else {
          console.log('[Tab Debug] Ignoring force_logout - not active or is duplicate');
        }
      }
    };

    // If no one responded after 500ms, this tab claims the active session
    const claimSessionTimeout = setTimeout(() => {
      const hasToken = localStorage.getItem("token");
      const alreadyActive = sessionStorage.getItem("tab_session_active") === "true";
      // Check current duplicate state - don't claim if already marked as duplicate
      if (hasToken && !alreadyActive && sessionStorage.getItem("tab_session_marked_duplicate") !== "true") {
        sessionStorage.setItem("tab_session_active", "true");
        setIsDuplicateTab(false);
        console.log('[Tab Debug] Claimed session after timeout');
      } else {
        console.log('[Tab Debug] Not claiming session - already marked as duplicate or not active');
      }
    }, 500);
    timeoutRef.current = claimSessionTimeout;

    return () => {
      clearTimeout(claimSessionTimeout);
      // Don't close channel here - keep it open for message handling
      // Channel will be closed on unmount (component cleanup)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Re-run when user changes (login/logout)

  // Cleanup channel on unmount only
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        channelRef.current.close();
      }
    };
  }, []);

  // The auth object now includes all the necessary functions
  const auth = {
    authenticate,    // Add authenticate function to the context
    reAuthenticate,  // Add reAuthenticate function to the context (optional)
    logout,          // Add logout function to the context
    checkTokenExpiry // Add checkTokenExpiry function to the context
  };

  const forceClaimSession = () => {
    // Notify other tabs to logout before claiming
    console.log('[Tab Debug] Force claim - posting force_logout');
    const channel = new BroadcastChannel("app_session");
    channel.postMessage("force_logout");
    // Give a small delay for the message to be received and processed before reloading
    setTimeout(() => {
      channel.close();
      // Don't set tab_session_active here - let the reload claim it naturally
      // This prevents race conditions where both tabs think they're active
      window.location.reload(); // Reload to ensure a clean state
    }, 300); // Increased delay to ensure other tab processes the logout
  };

  if (isDuplicateTab) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        padding: 20 
      }}>
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          backdropFilter: 'blur(10px)',
          padding: '50px 40px', 
          borderRadius: 20, 
          boxShadow: '0 10px 30px rgba(0,0,0,0.08), 0 20px 48px rgba(0,0,0,0.06)', 
          textAlign: 'center', 
          maxWidth: 460,
          border: '1px solid rgba(255, 255, 255, 0.6)'
        }}>
          <div style={{ 
            fontSize: 56, 
            marginBottom: 20,
            display: 'inline-block'
          }}>
            📑
          </div>
          <h1 style={{ 
            color: '#1a1a1a', 
            fontSize: '26px',
            fontWeight: '800',
            marginBottom: '16px',
            letterSpacing: '-0.5px'
          }}>
            Oops! App Already Open
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#4a5568', 
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            You are already using this app in another tab. To keep things running smoothly and avoid any mix-ups, only one tab can be open at a time.
          </p>
          
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{ 
              background: 'linear-gradient(90deg, #fff3cd 0%, #ffeeba 100%)', 
              color: '#856404', 
              padding: '16px 20px', 
              borderRadius: '12px', 
              fontSize: '14px',
              fontWeight: '600',
              border: '1px solid rgba(255, 222, 160, 0.5)',
            }}>
              👉 Please close other tabs if they are open.
            </div>

            <button 
              onClick={forceClaimSession}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '14px 24px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Force Open in This Tab
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
